import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput,
  Image, SafeAreaView, ActivityIndicator, Modal,
  Linking, Animated,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { useAuth } from '../private/AuthContext';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import PurchaseService from '../services/PurchaseService';

// Assistant IA Desk AI
import MayaFloatingButton from './maya/MayaFloatingButton';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

const documentTypes = [
  {
    id: 'all',
    title: 'Tous les documents',
    icon: 'file-document-multiple',
  },
  {
    id: 'concours',
    title: 'Sujets d\'Épreuves',
    icon: 'school',
  },
  {
    id: 'bords',
    title: 'Bords',
    icon: 'book-open-variant',
  },
  {
    id: 'documents',
    title: 'Documents Simples',
    icon: 'file-document',
  },
];

const languages = [
  { id: 'all', title: 'Toutes les langues' },
  { id: 'fr', title: 'Français' },
  { id: 'en', title: 'Anglais' }
];

export default function HomeScreen({ navigation }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [yearModalVisible, setYearModalVisible] = useState(false);
  const [languageModalVisible, setLanguageModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const { theme } = useTheme();
  const { user } = useAuth();

  // -- Tri mixte: priorité aux docs SANS displayOrder (nouveaux), sinon createdAt, puis displayOrder
const hasDisplayOrder = (d) =>
  typeof d?.displayOrder === 'number' && Number.isFinite(d.displayOrder);

const ts = (d) =>
  d?.createdAt?.toDate?.() ? d.createdAt.toDate().getTime() : 0;


  // --- Markdown léger + ouverture de lien Web/Android ---
const isWeb = typeof window !== 'undefined';
const isHttpUrl = (str) => /^https?:\/\//i.test(String(str || ''));

const openExternal = (url) => {
  if (!url) return;
  if (isWeb) window.open(url, '_blank', 'noopener,noreferrer');
  else Linking.openURL(url);
};

const MarkdownInline = ({ text = '', theme, numberOfLines = 2 }) => {
  const regex = /(\*\*[^*]+?\*\*|__[^_]+?__|\*[^*]+?\*|https?:\/\/\S+)/g;
  const parts = [];
  let lastIndex = 0, m;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    const token = m[0];
    if (token.startsWith('**')) parts.push({ type: 'bold', value: token.slice(2, -2) });
    else if (token.startsWith('__')) parts.push({ type: 'underline', value: token.slice(2, -2) });
    else if (token.startsWith('*')) parts.push({ type: 'italic', value: token.slice(1, -1) });
    else if (isHttpUrl(token)) parts.push({ type: 'link', value: token });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });

  return (
    <Text numberOfLines={numberOfLines} ellipsizeMode="tail" style={{ fontSize: 12, lineHeight: 16, color: theme.textSecondary }}>
      {parts.map((p, i) => {
        const style = {};
        if (p.type === 'bold') style.fontWeight = '700';
        if (p.type === 'italic') style.fontStyle = 'italic';
        if (p.type === 'underline') style.textDecorationLine = 'underline';
        if (p.type === 'link') {
          return (
            <Text
              key={`md-${i}`}
              style={{ textDecorationLine: 'underline', color: theme.primary }}
              onPress={() => openExternal(p.value)}
            >
              {p.value}
            </Text>
          );
        }
        return (
          <Text key={`md-${i}`} style={style}>
            {p.value}
          </Text>
        );
      })}
    </Text>
  );
};


  useEffect(() => {
    fetchDocuments();
  }, [selectedType, selectedYear, selectedLanguage, searchQuery]);

  // Vérifier les paiements en attente au démarrage
  useEffect(() => {
    if (user?.uid) {
      PurchaseService.checkPendingPayments(user.uid).then(confirmedPayments => {
        if (confirmedPayments.length > 0) {
          console.log(`${confirmedPayments.length} paiement(s) en attente confirmé(s) automatiquement`);
        }
      }).catch(err => {
        console.error('Erreur vérification paiements en attente:', err);
      });
    }
  }, [user?.uid]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
  
      let docs = [];
      const collections = selectedType === 'all'
        ? ['concours', 'bords', 'documents']
        : [selectedType];
  
      for (const collectionName of collections) {
        const refCol = collection(db, collectionName);
  
        // On ne met pas de orderBy ici : on trie côté client
        const constraints = [ where('status', '==', 'active') ];
        if (selectedLanguage !== 'all') {
          constraints.push(where('langue', '==', selectedLanguage));
        }
  
        const qy = query(refCol, ...constraints);
        const snap = await getDocs(qy);
  
        let items = snap.docs.map(d => ({
          id: d.id,
          type: collectionName,
          ...d.data(),
        }));
  
        // Filtre Année (côté client) pour les concours
        if (collectionName === 'concours' && selectedYear) {
          items = items.filter(it =>
            Array.isArray(it.documents) &&
            it.documents.some(x => Number(x?.annee) === Number(selectedYear))
          );
        }
  
        // Filtre texte
        const sq = (searchQuery || '').toLowerCase();
        if (sq) {
          items = items.filter(it => {
            const title = (it.titre || it.nomComplet || it.sigle || '').toLowerCase();
            const desc  = (it.description || '').toLowerCase();
            return title.includes(sq) || desc.includes(sq);
          });
        }
  
        docs.push(...items);
      }
  
      // ---- TRI FINAL (identique à Android) ----
      // 1) docs SANS displayOrder → en haut, triés par createdAt DESC
      // 2) docs AVEC displayOrder → displayOrder ASC, tiebreak createdAt DESC
      docs.sort((a, b) => {
        const aHas = typeof a?.displayOrder === 'number' && Number.isFinite(a.displayOrder);
        const bHas = typeof b?.displayOrder === 'number' && Number.isFinite(b.displayOrder);
        const aTime = a?.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : 0;
        const bTime = b?.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : 0;
  
        if (!aHas && bHas) return -1; // sans ordre d’abord
        if (aHas && !bHas) return  1;
  
        if (!aHas && !bHas) return bTime - aTime; // récents en haut
  
        if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
        return bTime - aTime;
      });
  
      setDocuments(docs);
    } catch (err) {
      console.error('fetchDocuments error:', err);
      setDocuments([]); // évite un état incohérent
    } finally {
      setLoading(false);
    }
  };
  

  const openWhatsApp = () => {
    // Numéro de téléphone du service d'assistance
    const phoneNumber = '+237671454061'; 
    const message = 'Bonjour, j\'ai besoin d\'aide sur MyConcours';
    Linking.openURL(`whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`);
  };

  const renderHeader = () => (
    <LinearGradient
      colors={[theme.primary, theme.primaryDark]}
      start={{ x: 0, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.header}
    >
      <View style={styles.headerTop}>
        <View style={styles.welcomeText}>
          <Text style={[styles.username, { color: '#fff' }]}>
            {user ? (user.userType === 'user' ? user.fullName : user.centerName) : 'Accueil'}
          </Text>
          <Text style={[styles.greeting, { color: '#fff' }]}>
            Bienvenue sur MyConcours
          </Text>
        </View>
        <TouchableOpacity style={[styles.notificationButton, { backgroundColor: theme.surface }]}
        onPress={() => navigation.navigate('NotificationsScreen')}
        >
          {/* <View style={[styles.notificationBadge, { backgroundColor: '#ff4444' }]}>
            <Text style={{ color: '#fff' }}>2</Text>
          </View> */}
          <MaterialCommunityIcons name="bell-outline" size={28} color={theme.primary} />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderYearModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={yearModalVisible}
      onRequestClose={() => setYearModalVisible(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setYearModalVisible(false)}
      >
        <View style={[styles.filterModalContent, { backgroundColor: theme.surface }]}>
          <Text style={[styles.modalTitle, { color: theme.text }]}>
            Sélectionner une année
          </Text>
          <ScrollView>
            <TouchableOpacity
              style={[styles.filterOption, 
                !selectedYear && styles.selectedFilter,
                { borderColor: theme.border }
              ]}
              onPress={() => {
                setSelectedYear(null);
                setYearModalVisible(false);
              }}
            >
              <Text style={[styles.filterText, { 
                color: !selectedYear ? theme.primary : theme.text 
              }]}>
                Toutes les années
              </Text>
              {!selectedYear && (
                <MaterialCommunityIcons name="check" size={24} color={theme.primary} />
              )}
            </TouchableOpacity>
            {years.map((year) => (
              <TouchableOpacity
                key={year}
                style={[styles.filterOption, 
                  selectedYear === year && styles.selectedFilter,
                  { borderColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedYear(year);
                  setYearModalVisible(false);
                }}
              >
                <Text style={[styles.filterText, { 
                  color: selectedYear === year ? theme.primary : theme.text 
                }]}>
                  {year}
                </Text>
                {selectedYear === year && (
                  <MaterialCommunityIcons name="check" size={24} color={theme.primary} />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderLanguageModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={languageModalVisible}
      onRequestClose={() => setLanguageModalVisible(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setLanguageModalVisible(false)}
      >
        <View style={[styles.filterModalContent, { backgroundColor: theme.surface }]}>
          <Text style={[styles.modalTitle, { color: theme.text }]}>
            Sélectionner une langue
          </Text>
          {languages.map((lang) => (
            <TouchableOpacity
              key={lang.id}
              style={[styles.filterOption, 
                selectedLanguage === lang.id && styles.selectedFilter,
                { borderColor: theme.border }
              ]}
              onPress={() => {
                setSelectedLanguage(lang.id);
                setLanguageModalVisible(false);
              }}
            >
              <Text style={[styles.filterText, { 
                color: selectedLanguage === lang.id ? theme.primary : theme.text 
              }]}>
                {lang.title}
              </Text>
              {selectedLanguage === lang.id && (
                <MaterialCommunityIcons name="check" size={24} color={theme.primary} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderTypeModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={typeModalVisible}
      onRequestClose={() => setTypeModalVisible(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setTypeModalVisible(false)}
      >
        <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              Sélectionner le type de document
            </Text>
            <TouchableOpacity 
              onPress={() => setTypeModalVisible(false)}
              style={styles.closeButton}
            >
              <MaterialCommunityIcons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>

          {documentTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={[
                styles.typeOption,
                selectedType === type.id && styles.selectedType,
                { borderColor: theme.border }
              ]}
              onPress={() => {
                setSelectedType(type.id);
                setTypeModalVisible(false);
              }}
            >
              <MaterialCommunityIcons 
                name={type.icon} 
                size={24} 
                color={selectedType === type.id ? theme.primary : theme.textSecondary} 
              />
              <Text 
                style={[
                  styles.typeText,
                  { color: selectedType === type.id ? theme.primary : theme.text }
                ]}
              >
                {type.title}
              </Text>
              {selectedType === type.id && (
                <MaterialCommunityIcons 
                  name="check" 
                  size={24} 
                  color={theme.primary} 
                />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderSearch = () => (
    <View style={[styles.searchSection, { backgroundColor: theme.surface }]}>
      <View style={[styles.searchBar, { backgroundColor: theme.background }]}>
        <MaterialCommunityIcons name="magnify" size={24} color={theme.textSecondary} />
        <TextInput
          style={[styles.searchInput, { color: theme.text }]}
          placeholder="Rechercher un document..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor={theme.textSecondary}
        />
      </View>
      
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll}>
        <TouchableOpacity
          style={[
            styles.filterChip,
            { 
              backgroundColor: selectedYear ? theme.primary : theme.background,
              borderColor: selectedYear ? theme.primary : theme.border 
            }
          ]}
          onPress={() => setYearModalVisible(true)}
        >
          <Text style={{
            color: selectedYear ? '#fff' : theme.textSecondary
          }}>
            {selectedYear ? selectedYear : 'Année'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterChip,
            { 
              backgroundColor: selectedType !== 'all' ? theme.primary : theme.background,
              borderColor: selectedType !== 'all' ? theme.primary : theme.border 
            }
          ]}
          onPress={() => setTypeModalVisible(true)}
        >
          <Text style={{
            color: selectedType !== 'all' ? '#fff' : theme.textSecondary
          }}>
            Type
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterChip,
            { 
              backgroundColor: selectedLanguage !== 'all' ? theme.primary : theme.background,
              borderColor: selectedLanguage !== 'all' ? theme.primary : theme.border 
            }
          ]}
          onPress={() => setLanguageModalVisible(true)}
        >
          <Text style={{
            color: selectedLanguage !== 'all' ? '#fff' : theme.textSecondary
          }}>
            Langue
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const getImageSource = (doc) => {
    switch (doc.type) {
      case 'bords':
        return doc.image;
      case 'concours':
      case 'documents':
        return doc.coverImage;
      default:
        return null;
    }
  };

  const getDocumentType = (doc) => {
    switch (doc.type) {
      case 'concours':
        return 'Épreuves';
      case 'bords':
        return 'Bord';
      case 'documents':
        return 'Document';
      default:
        return 'Document';
    }
  };

  const renderDocumentGrid = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>
          Documents Disponibles
        </Text>
      </View>

      <View style={styles.documentsGrid}>
        {loading ? (
          <ActivityIndicator size="large" color={theme.primary} style={styles.loader} />
        ) : documents.length > 0 ? (
          documents.map((doc) => (
            <TouchableOpacity 
              key={doc.id} 
              style={[styles.documentCard, {
                backgroundColor: theme.surface,
                borderColor: theme.border}]}
                onPress={() => navigation.navigate('DocumentDetailsScreenUser', { document: doc })}
              >
                <Image
                  source={{ uri: getImageSource(doc) }}
                  style={styles.documentImage}
                />
                <View style={styles.documentBadge}>
                  <Text style={styles.badgeText}>
                    {getDocumentType(doc)}
                  </Text>
                </View>
                <View style={styles.documentInfo}>
                  <Text style={[styles.documentName, { color: theme.text }]} numberOfLines={1}>
                    {doc.type === 'concours' ? (doc.sigle || doc.nomComplet) : doc.titre}
                  </Text>
                  <MarkdownInline text={doc.description} theme={theme} numberOfLines={2} />

                  {/* Affichage du prix pour les bords avec réduction */}
                  {doc.type === 'bords' && doc.prix && (
                    <View style={{ marginTop: 4 }}>
                      {parseFloat(doc.reductionPourcent || 0) > 0 ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Text style={[styles.documentPrice, { color: theme.danger, textDecorationLine: 'line-through', marginRight: 6, fontSize: 12 }]}>
                            {doc.prixInitial || doc.prix} FCFA
                          </Text>
                          <Text style={[styles.documentPrice, { color: theme.primary, fontWeight: 'bold' }]}>
                            {doc.prixFinal || doc.prix} FCFA
                          </Text>
                        </View>
                      ) : (
                        <Text style={[styles.documentPrice, { color: theme.primary }]}>
                          {doc.prix} FCFA
                        </Text>
                      )}
                    </View>
                  )}

                  {/* Badge de réduction pour les concours */}
                  {doc.type === 'concours' && (() => {
                    // Récupérer tous les documents (épreuves)
                    let allDocs = [];
                    if (doc.hasGroups && Array.isArray(doc.groups)) {
                      doc.groups.forEach(group => {
                        if (Array.isArray(group.documents)) {
                          allDocs.push(...group.documents);
                        }
                      });
                    } else if (Array.isArray(doc.documents)) {
                      allDocs = doc.documents;
                    }

                    // Vérifier si au moins un document a une réduction
                    const hasAnyReduction = allDocs.some(d =>
                      parseFloat(d.reductionPourcent || 0) > 0
                    );

                    if (!hasAnyReduction) return null;

                    return (
                      <View style={{
                        marginTop: 6,
                        backgroundColor: theme.danger,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 4,
                        alignSelf: 'flex-start'
                      }}>
                        <Text style={{ color: '#fff', fontSize: 11, fontWeight: 'bold' }}>
                          Réduction
                        </Text>
                      </View>
                    );
                  })()}
                </View>
              </TouchableOpacity>
            ))
          ) : (
            <View style={styles.noDocuments}>
              <MaterialCommunityIcons name="file-document" size={48} color={theme.textSecondary} />
              <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
                Aucun document disponible
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
        <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
          {renderHeader()}
          {renderSearch()}
          {renderDocumentGrid()}
          {renderTypeModal()}
          {renderYearModal()}
          {renderLanguageModal()}
          <View style={styles.bottomSpace} />
        </ScrollView>
        {/* Bouton flottant Desk AI (assistant IA) — uniquement si utilisateur connecté */}
        {user && (
          <MayaFloatingButton
            onPress={() => navigation.navigate('ChatMayaScreen')}
            bottom={186}
          />
        )}

        {/* Bouton WhatsApp */}
        <View style={styles.whatsappButtonContainer}>
          <TouchableOpacity
            style={styles.whatsappButton}
            onPress={openWhatsApp}
            activeOpacity={0.8}
          >
            <LinearGradient
              colors={['#165ca2', '#1976D2']}
              style={styles.whatsappButtonGradient}
            >
              <MaterialCommunityIcons name="whatsapp" size={28} color="#fff" />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }
  
  // const styles = StyleSheet.create({
  //   safeArea: {
  //     flex: 1,
  //   },
  //   container: {
  //     flex: 1,
  //   },
  //   header: {
  //     paddingTop: 20,
  //     paddingBottom: 30,
  //     paddingHorizontal: 20,
  //     borderBottomLeftRadius: 30,
  //     borderBottomRightRadius: 30,
  //   },
  //   headerTop: {
  //     flexDirection: 'row',
  //     justifyContent: 'space-between',
  //     alignItems: 'flex-start',
  //   },
  //   welcomeText: {
  //     flex: 1,
  //   },
  //   username: {
  //     fontSize: 24,
  //     fontWeight: 'bold',
  //   },
  //   greeting: {
  //     opacity: 0.9,
  //     fontSize: 14,
  //     marginTop: 4,
  //   },
  //   notificationButton: {
  //     position: 'relative',
  //     padding: 5,
  //     borderRadius: 10,
  //   },
  //   notificationBadge: {
  //     position: 'absolute',
  //     right: -7,
  //     top: -7,
  //     borderRadius: 10,
  //     width: 20,
  //     height: 20,
  //     justifyContent: 'center',
  //     alignItems: 'center',
  //     zIndex: 1,
  //   },
  //   searchSection: {
  //     padding: 20,
  //     margin: 20,
  //     marginTop: -20,
  //     borderRadius: 20,
  //     shadowOffset: {
  //       width: 0,
  //       height: 2,
  //     },
  //     shadowOpacity: 0.05,
  //     shadowRadius: 15,
  //     elevation: 5,
  //   },
  //   searchBar: {
  //     flexDirection: 'row',
  //     alignItems: 'center',
  //     borderRadius: 12,
  //     paddingHorizontal: 15,
  //   },
  //   searchInput: {
  //     flex: 1,
  //     paddingVertical: 12,
  //     marginLeft: 10,
  //     fontSize: 16,
  //   },
  //   filterScroll: {
  //     marginTop: 15,
  //   },
  //   filterChip: {
  //     paddingHorizontal: 16,
  //     paddingVertical: 8,
  //     borderRadius: 20,
  //     marginRight: 10,
  //     borderWidth: 1,
  //   },
  //   section: {
  //     padding: 20,
  //   },
  //   sectionHeader: {
  //     flexDirection: 'row',
  //     justifyContent: 'space-between',
  //     alignItems: 'center',
  //     marginBottom: 15,
  //   },
  //   sectionTitle: {
  //     fontSize: 20,
  //     fontWeight: 'bold',
  //   },
  //   documentsGrid: {
  //     flexDirection: 'row',
  //     flexWrap: 'wrap',
  //     justifyContent: 'space-between',
  //     gap: 15,
  //   },
  //   documentCard: {
  //     width: '48%',
  //     borderRadius: 16,
  //     overflow: 'hidden',
  //     borderWidth: 1,
  //   },
  //   documentImage: {
  //     width: '100%',
  //     height: 120,
  //     resizeMode: 'cover',
  //   },
  //   documentBadge: {
  //     position: 'absolute',
  //     top: 10,
  //     right: 10,
  //     backgroundColor: 'rgba(0, 0, 0, 0.6)',
  //     paddingHorizontal: 8,
  //     paddingVertical: 4,
  //     borderRadius: 12,
  //   },
  //   badgeText: {
  //     color: '#fff',
  //     fontSize: 12,
  //     fontWeight: '500',
  //   },
  //   documentInfo: {
  //     padding: 12,
  //   },
  //   documentName: {
  //     fontSize: 15,
  //     fontWeight: 'bold',
  //     marginBottom: 4,
  //   },
  //   documentDesc: {
  //     fontSize: 12,
  //     marginBottom: 8,
  //     lineHeight: 16,
  //   },
  //   documentPrice: {
  //     fontSize: 14,
  //     fontWeight: '600',
  //   },
  //   modalOverlay: {
  //     flex: 1,
  //     backgroundColor: 'rgba(0, 0, 0, 0.5)',
  //     justifyContent: 'flex-end',
  //   },
  //   modalContent: {
  //     borderTopLeftRadius: 20,
  //     borderTopRightRadius: 20,
  //     padding: 20,
  //     maxHeight: '80%',
  //   },
  //   filterModalContent: {
  //     width: '80%',
  //     backgroundColor: 'white',
  //     borderRadius: 20,
  //     padding: 20,
  //     alignSelf: 'center',
  //     marginTop: 'auto',
  //     marginBottom: 'auto',
  //     maxHeight: '80%',
  //   },
  //   modalHeader: {
  //     flexDirection: 'row',
  //     justifyContent: 'space-between',
  //     alignItems: 'center',
  //     marginBottom: 20,
  //   },
  //   modalTitle: {
  //     fontSize: 18,
  //     fontWeight: 'bold',
  //     marginBottom: 15,
  //   },
  //   closeButton: {
  //     padding: 5,
  //   },
  //   typeOption: {
  //     flexDirection: 'row',
  //     alignItems: 'center',
  //     padding: 15,
  //     borderRadius: 12,
  //     marginBottom: 10,
  //     borderWidth: 1,
  //   },
  //   filterOption: {
  //     flexDirection: 'row',
  //     alignItems: 'center',
  //     justifyContent: 'space-between',
  //     padding: 15,
  //     borderRadius: 12,
  //     marginBottom: 10,
  //     borderWidth: 1,
  //   },
  //   filterText: {
  //     fontSize: 16,
  //   },
  //   selectedType: {
  //     backgroundColor: 'rgba(22, 92, 162, 0.1)',
  //   },
  //   selectedFilter: {
  //     backgroundColor: 'rgba(22, 92, 162, 0.1)',
  //   },
  //   typeText: {
  //     flex: 1,
  //     marginLeft: 15,
  //     fontSize: 16,
  //   },
  //   loader: {   
  //     marginTop: 40,
  //     alignItems: 'center',
  //     justifyContent: 'center',
  //     minHeight: 200,
  //   },
  //   noDocuments: {
  //     alignItems: 'center',
  //     justifyContent: 'center',
  //     minHeight: 200,
  //     width: '100%',
  //   },
  //   emptyText: {
  //     textAlign: 'center',
  //     marginTop: 16,
  //     fontSize: 16,
  //   },
  //   bottomSpace: {
  //     height: 100,
  //   },
  // });

const styles = StyleSheet.create({
safeArea: {
  flex: 1,
},
container: {
  flex: 1,
},
header: {
  paddingTop: 20,
  paddingBottom: 30,
  paddingHorizontal: 20,
  borderBottomLeftRadius: 30,
  borderBottomRightRadius: 30,
},
headerTop: {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
},
welcomeText: {
  flex: 1,
},
username: {
  fontSize: 24,
  fontWeight: 'bold',
},
greeting: {
  opacity: 0.9,
  fontSize: 14,
  marginTop: 4,
},
notificationButton: {
  position: 'relative',
  padding: 5,
  borderRadius: 10,
},
notificationBadge: {
  position: 'absolute',
  right: -7,
  top: -7,
  borderRadius: 10,
  width: 20,
  height: 20,
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
},
searchSection: {
  padding: 20,
  margin: 20,
  marginTop: -20,
  borderRadius: 20,
  shadowOffset: {
    width: 0,
    height: 2,
  },
  shadowOpacity: 0.05,
  shadowRadius: 15,
  elevation: 5,
},
searchBar: {
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: 12,
  paddingHorizontal: 15,
},
searchInput: {
  flex: 1,
  paddingVertical: 12,
  marginLeft: 10,
  fontSize: 16,
},
filterScroll: {
  marginTop: 15,
},
filterChip: {
  paddingHorizontal: 16,
  paddingVertical: 8,
  borderRadius: 20,
  marginRight: 10,
  borderWidth: 1,
},
section: {
  padding: 20,
},
sectionHeader: {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 15,
},
sectionTitle: {
  fontSize: 20,
  fontWeight: 'bold',
},
documentsGrid: {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
},
documentCard: {
  width: '48%',
  marginBottom: 15,
  borderRadius: 16,
  overflow: 'hidden',
  borderWidth: 1,
},
documentImage: {
  width: '100%',
  height: 120,
},
documentBadge: {
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: 12,
},
badgeText: {
  color: '#fff',
  fontSize: 12,
  fontWeight: '500',
},
documentInfo: {
  padding: 12,
},
documentName: {
  fontSize: 16,
  fontWeight: 'bold',
  marginBottom: 4,
},
documentDesc: {
  fontSize: 12,
  marginBottom: 8,
  lineHeight: 16,
},
documentPrice: {
  fontSize: 14,
  fontWeight: '600',
},
loader: {   
  marginTop: 40,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 200,
},
noDocuments: {
  textAlign: 'center',
  marginTop: 40,
  fontSize: 16,
  minHeight: 200,
  marginTop: 40,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 200,
},
emptyText: {
  textAlign: 'center',
  marginTop: 40,
  fontSize: 16,
},
modalOverlay: {
  flex: 1,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  justifyContent: 'flex-end',
},
modalContent: {
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  padding: 20,
  maxHeight: '80%',
},
filterModalContent: {
  width: '80%',
  backgroundColor: 'white',
  borderRadius: 20,
  padding: 20,
  alignSelf: 'center',
  marginTop: 'auto',
  marginBottom: 'auto',
},
modalHeader: {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
},
modalTitle: {
  fontSize: 18,
  fontWeight: 'bold',
  marginBottom: 15,
},
closeButton: {
  padding: 5,
},
typeOption: {
  flexDirection: 'row',
  alignItems: 'center',
  padding: 15,
  borderRadius: 12,
  marginBottom: 10,
  borderWidth: 1,
},
filterOption: {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 15,
  borderRadius: 12,
  marginBottom: 10,
  borderWidth: 1,
},
filterText: {
  fontSize: 16,
},
selectedType: {
  backgroundColor: 'rgba(22, 92, 162, 0.1)',
},
selectedFilter: {
  backgroundColor: 'rgba(22, 92, 162, 0.1)',
},
typeText: {
  flex: 1,
  marginLeft: 15,
  fontSize: 16,
},
bottomSpace: {
  height: 100,
},

// Styles pour le bouton WhatsApp
whatsappButtonContainer: {
  position: 'absolute',
  bottom: 110,
  right: 20,
  zIndex: 999,
},
whatsappButton: {
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
},
whatsappButtonGradient: {
  width: 60,
  height: 60,
  borderRadius: 30,
  justifyContent: 'center',
  alignItems: 'center',
  shadowColor: '#000',
  shadowOffset: {
    width: 0,
    height: 3,
  },
  shadowOpacity: 0.27,
  shadowRadius: 4.65,
  elevation: 6,
},
});